import { useCallback, useEffect, useState } from 'react';
import { hasLocalPendingWorkForDisplay } from '@/services/syncService';

export const describePendingSends = (online: boolean, pending: boolean | null): string | null => {
  if (!pending) return null;
  return online
    ? 'Há dados aguardando envio. Você pode continuar usando o app.'
    : 'Sem internet. Os envios serão retomados quando a conexão voltar.';
};

// O fechamento vale nesta visita à Home; ciclos de sync não o reabrem.
// Apenas observa a fila: não altera coleta, envio, diagnóstico ou resultado do sync.
export function useHomeSyncStatus({ userId, companyId, ready, online, busy, lastSyncDate }: {
  userId: string | null | undefined;
  companyId: string | null | undefined;
  ready: boolean;
  online: boolean;
  busy: boolean;
  lastSyncDate: unknown;
}) {
  const [refreshKey, setRefreshKey] = useState(0);
  const refresh = useCallback(() => setRefreshKey(value => value + 1), []);
  const scope = `${companyId ?? ''}:${userId ?? ''}`;
  const condition = `${scope}:${online ? 'online' : 'offline'}`;
  const [dismissedCondition, setDismissedCondition] = useState<string | null>(null);
  const [pending, setPending] = useState<{ scope: string; value: boolean | null } | null>(null);

  useEffect(() => { setDismissedCondition(null); }, [scope]);

  useEffect(() => {
    let active = true;
    if (!ready || !userId || !companyId || busy) return;
    void hasLocalPendingWorkForDisplay(companyId, userId).then(
      value => {
        if (!active) return;
        setPending({ scope, value });
        if (!value) setDismissedCondition(null);
      },
      () => { if (active) setPending({ scope, value: null }); }
    );
    return () => { active = false; };
  }, [scope, companyId, userId, ready, online, busy, lastSyncDate, refreshKey]);

  const pendingMessage = ready && userId && companyId
    ? describePendingSends(online, pending?.scope === scope ? pending.value : null)
    : null;
  const noticeVisible = Boolean(pendingMessage) && !busy && dismissedCondition !== condition;

  return {
    pendingMessage,
    refresh,
    noticeVisible,
    dismissNotice: () => setDismissedCondition(condition),
    toggleNotice: () => {
      if (pendingMessage && !busy) setDismissedCondition(current => current === condition ? null : condition);
    },
  };
}
